"use client";

import React from "react";

import { cn } from "../lib/utils";
import { Button, ButtonProps } from "../ui/button";
import { DialogClose } from "../ui/dialog";

interface ActionButtonsProps extends React.HTMLAttributes<HTMLDivElement> {
  actionButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  hasCancelButton?: boolean;
  hasActionButton?: boolean;
  inDialog?: boolean;
}

/**
 * ActionButtons will only show when a `ButtonProps` object
 * is passed to either the `cancelButtonProps` or `actionButtonProps` prop.
 *
 * @param cancelButtonProps - Props for the Cancel button.
 * @param actionButtonProps - Props for the Action button.
 * @param hasCancelButton - Whether to show the Cancel button.
 * @param hasActionButton - Whether to show the Action button.
 * @param inDialog - When `true`, the Cancel button will close its parent Dialog
 * without having to explicitly change its `open` state.
 */
const ActionButtons = React.forwardRef<HTMLDivElement, ActionButtonsProps>(
  (
    {
      className,
      cancelButtonProps = {},
      actionButtonProps,
      hasCancelButton = true,
      hasActionButton = true,
      inDialog,
      ...props
    },
    ref,
  ) => {
    if (!hasCancelButton && !hasActionButton) return null;
    if (!cancelButtonProps && !actionButtonProps) return null;

    const cancelButton = inDialog ? (
      <DialogClose asChild>
        <Button {...{ variant: "outline", ...cancelButtonProps }}>
          {cancelButtonProps?.children || "Cancel"}
        </Button>
      </DialogClose>
    ) : (
      <Button {...{ variant: "outline", ...cancelButtonProps }}>
        {cancelButtonProps?.children || "Cancel"}
      </Button>
    );

    const actionButton = (
      <Button {...actionButtonProps}>
        {actionButtonProps?.children || "Submit"}
      </Button>
    );

    return (
      <div className={cn("flex gap-2", className)} ref={ref} {...props}>
        {hasCancelButton && cancelButtonProps && cancelButton}
        {hasActionButton && actionButtonProps && actionButton}
      </div>
    );
  },
);
ActionButtons.displayName = "ActionButtons";

export { ActionButtons, type ActionButtonsProps };
